import { useEffect, useRef, useState } from "react";

import { cn } from "@src/shared/lib/utils";

const BAR_COUNT = 10;
const MIN_BAR_HEIGHT_PX = 4;
const MAX_BAR_HEIGHT_PX = 16;
// Small FFT so `frequencyBinCount` (fftSize / 2 = 32) divides cleanly across
// the 10 bars — this is a coarse spectrum, not a real audiogram, just enough
// resolution to make each bar reflect a different frequency band instead of
// all 10 repeating one scalar level.
const FFT_SIZE = 64;
// Static, non-animated fallback pattern shown under `prefers-reduced-motion:
// reduce` — alternating heights so it still *reads* as a recording glyph
// rather than a flat line, but never moves.
const REDUCED_MOTION_PATTERN = [8, 12, 8, 14, 8, 12, 8, 14, 8, 12];

function getAudioContextCtor(): typeof AudioContext | undefined {
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext
  );
}

function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true,
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefersReduced(query.matches);
    onChange();
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return prefersReduced;
}

export interface VoiceAudioMeterProps {
  /** Whether recording is currently in progress — the meter only reads the stream while this is true. */
  active: boolean;
  /**
   * Returns the live microphone `MediaStream`, e.g. `UseVoiceInputResult["getMediaStream"]`.
   * Read once when `active` becomes `true`; this component is expected to unmount between
   * recordings (the composer swaps it out for the text input when not recording), so it does not
   * watch for the stream being swapped out from under an already-mounted, already-active meter.
   */
  getMediaStream: () => MediaStream | null;
  className?: string;
}

/**
 * Real-time microphone level meter, driven by a Web Audio `AnalyserNode` tapped off the live
 * recording `MediaStream` — not a decorative animation. A waveform that moves regardless of actual
 * microphone input would let a clinician believe recording is working when the mic is muted or
 * dead; this only moves when the mic actually picks something up. Purely decorative to assistive
 * tech (`aria-hidden`) — the accessible "recording" signal lives in the sibling status text.
 */
export function VoiceAudioMeter({
  active,
  getMediaStream,
  className,
}: VoiceAudioMeterProps) {
  const barRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const setBarHeights = (heights: number[]) => {
      barRefs.current.forEach((bar, index) => {
        if (bar) bar.style.height = `${heights[index] ?? MIN_BAR_HEIGHT_PX}px`;
      });
    };

    if (!active) {
      setBarHeights(Array(BAR_COUNT).fill(MIN_BAR_HEIGHT_PX));
      return undefined;
    }

    const stream = getMediaStream();
    if (!stream) {
      setBarHeights(Array(BAR_COUNT).fill(MIN_BAR_HEIGHT_PX));
      return undefined;
    }

    if (prefersReducedMotion) {
      setBarHeights(REDUCED_MOTION_PATTERN);
      return undefined;
    }

    const AudioContextCtor = getAudioContextCtor();
    if (!AudioContextCtor) {
      setBarHeights(REDUCED_MOTION_PATTERN);
      return undefined;
    }

    const audioContext = new AudioContextCtor();
    void audioContext.resume();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = FFT_SIZE;
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const bins = new Uint8Array(analyser.frequencyBinCount);
    const binsPerBar = Math.max(
      1,
      Math.floor(analyser.frequencyBinCount / BAR_COUNT),
    );
    let frameId: number;

    const tick = () => {
      analyser.getByteFrequencyData(bins);

      const heights = Array.from({ length: BAR_COUNT }, (_, barIndex) => {
        const start = barIndex * binsPerBar;
        const end = Math.min(start + binsPerBar, bins.length);
        let sum = 0;
        for (let i = start; i < end; i += 1) sum += bins[i];
        const average = end > start ? sum / (end - start) : 0;
        return (
          MIN_BAR_HEIGHT_PX +
          (average / 255) * (MAX_BAR_HEIGHT_PX - MIN_BAR_HEIGHT_PX)
        );
      });

      setBarHeights(heights);
      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(frameId);
      source.disconnect();
      analyser.disconnect();
      void audioContext.close();
    };
  }, [active, getMediaStream, prefersReducedMotion]);

  return (
    <div
      aria-hidden="true"
      className={cn(
        "flex h-4 shrink items-center gap-0.5 overflow-hidden",
        className,
      )}
      data-testid="voice-audio-meter"
    >
      {Array.from({ length: BAR_COUNT }, (_, index) => (
        <span
          className={cn(
            "w-[3px] shrink-0 rounded-full bg-success motion-reduce:transition-none",
            "transition-[height] duration-100 ease-out",
          )}
          key={index}
          ref={(element) => {
            barRefs.current[index] = element;
          }}
          style={{ height: MIN_BAR_HEIGHT_PX }}
        />
      ))}
    </div>
  );
}
